// TEMPORARY - scanner engine benchmark, remove after arbitration

// Engines are pulled as IIFE bundles from a CDN instead of being imported from npm: webpack 4 cannot parse the
// `import.meta` used by their ESM builds. This also keeps package.json and vue.config.js untouched.
const ZXING_BASE = 'https://cdn.jsdelivr.net/npm/zxing-wasm@3.1.4'
const QUAGGA_URL = 'https://cdn.jsdelivr.net/npm/@ericblade/quagga2@1.12.1/dist/quagga.min.js'
const JSQR_URL = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js'
// Pinned rather than the /4.x/ alias, which answers 301. The npm builds of OpenCV target bundlers and leave
// `window.cv` empty when loaded from a plain script tag, so the official distribution is the only usable one.
const OPENCV_URL = 'https://docs.opencv.org/4.13.0/opencv.js'

const SCRIPT_TIMEOUT_MS = 10000

// zxing-wasm resolves its own .wasm against fastly.jsdelivr.net, a host hardcoded in the bundle and different from the
// one serving its script. On iOS that second host was unreachable while the script loaded, and the module aborted on an
// opaque Emscripten message. Supplying the binary ourselves keeps every request on a single proven host, and turns a
// failure into a readable HTTP status instead.
const ZXING_WASM_URL = `${ZXING_BASE}/dist/reader/zxing_reader.wasm`

let pending_zxing_binary = null

function loadZXingBinary() {
    if (!pending_zxing_binary) {
        // Memoized for its identity, not merely its bytes: prepareZXingModule compares overrides by reference, so a
        // fresh ArrayBuffer on each run would tear down and re-instantiate an already initialised module.
        pending_zxing_binary = fetch(ZXING_WASM_URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`)
                }

                return response.arrayBuffer()
            })
            .catch((error) => {
                pending_zxing_binary = null
                throw new Error(`Téléchargement du wasm impossible (${error.message}) : ${ZXING_WASM_URL}`)
            })
    }

    return pending_zxing_binary
}

export const FORMAT_MODES = {
    all: {
        label: 'Tous formats',
        description:
            'Toutes les symbologies connues du moteur. À garder tant que les formats réellement imprimés sur les étiquettes ne sont pas établis',
    },
    production: {
        label: 'QR + Code 128',
        description: 'À n’activer qu’une fois les formats utiles connus : décodage plus rapide et moins de risque de mauvaise lecture',
    },
}

const PRODUCTION_FORMATS = {
    zxing: ['QRCode', 'Code128'],
    native: ['qr_code', 'code_128'],
}

// Restricting formats keeps the comparison fair: every extra symbology costs decode time and adds misread risk.
// The 'all' mode trades both away to answer a different question - what symbology is actually printed on this label.
const ZXING_SHARED_OPTIONS = {
    maxNumberOfSymbols: 1,
    tryHarder: true,
    tryRotate: true,
    tryInvert: true,
    // The camera layer already hands over a native-resolution crop; an internal downscale would destroy dense Code 128 bars.
    tryDownscale: false,
}

// Defaults to the widest set: a benchmark that silently skips the symbology under test is worse than a slow one.
let format_mode = 'all'

export function setFormatMode(mode) {
    format_mode = mode
}

export function getFormatMode() {
    return format_mode
}

const pending_scripts = {}

function loadScript(url, timeout_ms) {
    const limit = timeout_ms || SCRIPT_TIMEOUT_MS

    if (!pending_scripts[url]) {
        pending_scripts[url] = new Promise((resolve, reject) => {
            const script = document.createElement('script')

            const timeout_id = setTimeout(() => {
                delete pending_scripts[url]
                reject(new Error(`Délai dépassé (${limit} ms) au chargement de ${url}`))
            }, limit)

            script.src = url
            script.async = true
            script.onload = () => {
                clearTimeout(timeout_id)
                resolve()
            }
            script.onerror = () => {
                clearTimeout(timeout_id)
                delete pending_scripts[url]
                reject(new Error(`Chargement impossible : ${url}`))
            }

            document.head.appendChild(script)
        })
    }

    return pending_scripts[url]
}

// A blank frame that decodes to nothing. Running one real decode during load() keeps engine warm-up out of the measurements,
// which matters most for BarcodeDetector: on Android its ML Kit backend is fetched on the first call and returns no result
// until that download completes.
function createWarmUpImageData() {
    const canvas = document.createElement('canvas')
    canvas.width = 64
    canvas.height = 64

    const context = canvas.getContext('2d')
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)

    return context.getImageData(0, 0, canvas.width, canvas.height)
}

// Quagga2 only accepts a URL, so its frames have to be re-encoded. Measured at ~3 ms for a 924x300 frame.
function toCanvas(image_data) {
    const canvas = document.createElement('canvas')
    canvas.width = image_data.width
    canvas.height = image_data.height
    canvas.getContext('2d').putImageData(image_data, 0, 0)

    return canvas
}

const nativeEngine = {
    id: 'native',
    label: 'BarcodeDetector (natif)',
    license: 'API navigateur',
    source: 'Intégré au navigateur — aucun téléchargement',
    unsupported_reason: '',
    supported_formats: [],
    detectors: {},

    async isSupported() {
        if (!('BarcodeDetector' in window)) {
            this.unsupported_reason = 'BarcodeDetector n’est pas implémenté par ce navigateur'
            return false
        }

        this.supported_formats = await window.BarcodeDetector.getSupportedFormats()
        const missing = PRODUCTION_FORMATS.native.filter((format) => !this.supported_formats.includes(format))

        if (missing.length) {
            this.unsupported_reason = `Formats non pris en charge : ${missing.join(', ')}`
            return false
        }

        return true
    },

    // Detectors are cached per mode: rebuilding one on every frame is a known performance killer.
    getDetector() {
        if (!this.detectors[format_mode]) {
            const formats = format_mode === 'all' ? this.supported_formats : PRODUCTION_FORMATS.native
            this.detectors[format_mode] = new window.BarcodeDetector({ formats: formats })
        }

        return this.detectors[format_mode]
    },

    async load() {
        await this.detect(createWarmUpImageData())
    },

    async detect(image_data) {
        const codes = await this.getDetector().detect(image_data)

        if (!codes.length) {
            return null
        }

        return { text: codes[0].rawValue, format: codes[0].format }
    },
}

const zxingEngine = {
    id: 'zxing',
    label: 'zxing-wasm 3.1.4',
    license: 'Apache-2.0',
    source: 'zxing-cpp compilé en WebAssembly',
    unsupported_reason: '',

    async isSupported() {
        return true
    },

    async load() {
        await loadScript(`${ZXING_BASE}/dist/iife/reader/index.js`)
        await window.ZXingWASM.prepareZXingModule({ overrides: { wasmBinary: await loadZXingBinary() }, fireImmediately: true })
        await this.detect(createWarmUpImageData())
    },

    async detect(image_data) {
        // Omitting `formats` altogether is how zxing-wasm is told to try every symbology it knows.
        const options =
            format_mode === 'all' ? ZXING_SHARED_OPTIONS : Object.assign({ formats: PRODUCTION_FORMATS.zxing }, ZXING_SHARED_OPTIONS)
        const results = await window.ZXingWASM.readBarcodes(image_data, options)

        if (!results.length) {
            return null
        }

        return { text: results[0].text, format: results[0].format }
    },
}

const QUAGGA_READERS = {
    production: ['code_128_reader'],
    all: [
        'code_128_reader',
        'code_39_reader',
        'code_93_reader',
        'i2of5_reader',
        'ean_reader',
        'ean_8_reader',
        'upc_reader',
        'upc_e_reader',
        'codabar_reader',
    ],
}

// Quagga2 silently never fires its callback on input it dislikes, which would hang the scan loop for good.
const QUAGGA_DECODE_TIMEOUT_MS = 5000

const quaggaEngine = {
    id: 'quagga',
    label: 'Quagga2 1.12.1',
    license: 'MIT',
    source: 'Décodeur 1D en JS pur — ne lit pas les QR codes',
    unsupported_reason: '',

    async isSupported() {
        return true
    },

    async load() {
        await loadScript(QUAGGA_URL)
        await this.detect(createWarmUpImageData())
    },

    detect(image_data) {
        // The default locator (halfSample on, medium patches) misses even a clean Code 128; these settings are required.
        const config = {
            src: toCanvas(image_data).toDataURL('image/png'),
            numOfWorkers: 0,
            locate: true,
            locator: { patchSize: 'large', halfSample: false },
            decoder: { readers: QUAGGA_READERS[format_mode] || QUAGGA_READERS.all },
        }

        return new Promise((resolve, reject) => {
            const timeout_id = setTimeout(() => reject(new Error('Quagga2 n’a pas répondu')), QUAGGA_DECODE_TIMEOUT_MS)

            window.Quagga.decodeSingle(config, (result) => {
                clearTimeout(timeout_id)

                if (!result || !result.codeResult) {
                    resolve(null)
                    return
                }

                resolve({ text: result.codeResult.code, format: result.codeResult.format })
            })
        })
    },
}

const jsqrEngine = {
    id: 'jsqr',
    label: 'jsQR 1.4.0',
    license: 'Apache-2.0',
    source: 'Décodeur QR en JS pur — ne lit pas les codes-barres 1D',
    unsupported_reason: '',

    async isSupported() {
        return true
    },

    async load() {
        await loadScript(JSQR_URL)
        await this.detect(createWarmUpImageData())
    },

    async detect(image_data) {
        const result = window.jsQR(image_data.data, image_data.width, image_data.height, { inversionAttempts: 'attemptBoth' })

        if (!result) {
            return null
        }

        return { text: result.data, format: 'QRCode' }
    },
}

const OPENCV_LOAD_TIMEOUT_MS = 60000
const OPENCV_READY_POLL_MS = 100

const opencvEngine = {
    id: 'opencv',
    label: 'OpenCV.js QRCodeDetector',
    license: 'Apache-2.0',
    source: 'Détecteur QR d’OpenCV (~3,5 Mo) — ne lit pas les codes-barres 1D',
    unsupported_reason: '',
    detector: null,

    async isSupported() {
        return true
    },

    async load() {
        await loadScript(OPENCV_URL, OPENCV_LOAD_TIMEOUT_MS)

        // The script tag resolves long before the wasm runtime is usable, and the module functions appear before the
        // Embind classes are registered - polling on matFromImageData alone yields "is not a constructor". Attempting
        // the construction is the only check that proves the runtime is actually ready.
        const deadline = performance.now() + OPENCV_LOAD_TIMEOUT_MS
        while (!this.detector) {
            if (performance.now() > deadline) {
                throw new Error('OpenCV.js n’a pas fini de s’initialiser')
            }

            try {
                this.detector = new window.cv.QRCodeDetector()
            } catch (error) {
                await new Promise((resolve) => setTimeout(resolve, OPENCV_READY_POLL_MS))
            }
        }
        await this.detect(createWarmUpImageData())
    },

    async detect(image_data) {
        const mat = window.cv.matFromImageData(image_data)

        try {
            const text = this.detector.detectAndDecode(mat)

            return text ? { text: text, format: 'QRCode' } : null
        } finally {
            // Emscripten heap objects are not garbage collected; skipping this leaks a full frame per attempt.
            mat.delete()
        }
    },
}

export default [zxingEngine, nativeEngine, quaggaEngine, jsqrEngine, opencvEngine]
